import type { TFunction } from 'i18next'
import { Activity, ChevronRight, FileText, FolderOpen, Network, Trash2 } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { Case } from '@/services/api'
import { CaseStatusBadge } from './CaseStatusBadge'

function formatRelativeTime(dateString: string, t: TFunction): string {
  const date = new Date(dateString)
  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000)
  if (diffMins < 1) return t('overview.justNow')
  if (diffMins < 60) return t('overview.relative.minutes', { n: diffMins })
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return t('overview.relative.hours', { n: diffHours })
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return t('overview.relative.days', { n: diffDays })
  return date.toLocaleDateString()
}

export function CaseCard({
  caseData,
  onOpen,
  onDelete,
}: {
  caseData: Case
  onOpen: () => void
  onDelete: (e: MouseEvent) => void
}) {
  const { t } = useTranslation()
  return (
    <Card
      className="group cursor-pointer bg-card/50 border-border hover:border-border hover:bg-muted/50 transition-all duration-200 max-md:gap-3! max-md:py-4"
      onClick={onOpen}
    >
      <CardHeader className="max-md:px-4 md:pb-3">
        <div className="flex items-start justify-between max-md:gap-2">
          <div className="flex items-center gap-3 max-md:min-w-0">
            <div className="p-2 rounded-lg bg-muted group-hover:bg-accent transition-colors max-md:shrink-0">
              <FolderOpen className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="max-md:min-w-0">
              <CardTitle className="text-base text-foreground transition-colors max-md:break-words md:text-lg">
                {caseData.name}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {t('cases.dashboard.updatedAgo', {
                  time: formatRelativeTime(caseData.updated_at, t),
                })}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 max-md:shrink-0">
            {/* Below md the status sits in the bottom row, so the case name keeps the width. */}
            <span className="hidden md:contents">
              <CaseStatusBadge status={caseData.status} />
            </span>
            {/* Hover-revealed from md; always visible where there is no hover (phones, touch tablets). */}
            <Button
              variant="ghost"
              size="icon"
              className="size-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-opacity md:size-8 md:opacity-0 md:group-hover:opacity-100 [@media(hover:none)]:opacity-100!"
              onClick={onDelete}
              aria-label={t('cases.delete.title')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 max-md:px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {caseData.document_count ?? 0}
            </span>
            <span className="flex items-center gap-1">
              <Network className="h-4 w-4" />
              {caseData.entity_count ?? 0}
            </span>
            <span className="flex items-center gap-1">
              <Activity className="h-4 w-4" />
              {caseData.workflow_count ?? 0}
            </span>
          </div>
          <span className="mr-2 ml-auto flex md:hidden">
            <CaseStatusBadge status={caseData.status} />
          </span>
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent-foreground transition-colors" />
        </div>
      </CardContent>
    </Card>
  )
}
